import { Command } from 'commander';
import { AutoDev } from '../core/auto-dev';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import pc from 'picocolors';
import path from 'path';

export function createAutoDevCommand(): Command {
  const command = new Command('auto-dev');
  
  command
    .description('24-hour automated development mode')
    .alias('ad');

  // Start auto-dev mode
  command
    .command('start')
    .description('Start automated development mode')
    .option('--max-tasks <number>', 'Maximum number of tasks to process')
    .option('--max-hours <hours>', 'Maximum hours to run (default: 24)')
    .option('--pause <seconds>', 'Pause between tasks in seconds (default: 30)')
    .option('--no-split', 'Disable automatic task splitting')
    .option('--auto-commit', 'Automatically commit changes after tasks')
    .option('--dry-run', 'Preview mode without actual execution')
    .option('--log-file <file>', 'Log output to file')
    .action(withErrorHandling(async (options) => {
      console.log(pc.bold('\n🤖 Starting Auto-Dev Mode\n'));
      
      const autoDev = new AutoDev({
        maxIterations: options.maxTasks ? parseInt(options.maxTasks) : undefined,
        maxDuration: options.maxHours ? parseInt(options.maxHours) * 60 : undefined,
        pauseBetweenTasks: options.pause ? parseInt(options.pause) : 30,
        autoSplit: options.split !== false,
        autoCommit: options.autoCommit || false,
        dryRun: options.dryRun || false,
        logFile: options.logFile
      });

      // Show configuration
      console.log(pc.dim('Configuration:'));
      console.log(pc.dim(`  Max tasks: ${options.maxTasks || 'unlimited'}`));
      console.log(pc.dim(`  Max duration: ${options.maxHours || 24} hours`));
      console.log(pc.dim(`  Pause between tasks: ${options.pause || 30} seconds`));
      console.log(pc.dim(`  Auto split: ${options.split !== false}`));
      console.log(pc.dim(`  Auto commit: ${options.autoCommit || false}`));
      console.log(pc.dim(`  Mode: ${options.dryRun ? 'DRY RUN' : 'LIVE'}`));
      
      if (options.logFile) {
        console.log(pc.dim(`  Log file: ${options.logFile}`));
      }
      
      console.log();

      // Set up graceful shutdown
      let isShuttingDown = false;
      const shutdown = async () => {
        if (isShuttingDown) return;
        isShuttingDown = true;
        
        console.log(pc.yellow('\n\nShutting down auto-dev mode...'));
        await autoDev.stop();
        
        const status = autoDev.getStatus();
        console.log(pc.bold('\n📊 Final Status:'));
        console.log(`  Completed: ${status.completedTasks} tasks`);
        console.log(`  Failed: ${status.failedTasks} tasks`);
        console.log(`  Duration: ${Math.round((Date.now() - (status.startTime?.getTime() || 0)) / 60000)} minutes`);
        
        process.exit(0);
      };

      process.on('SIGINT', shutdown);
      process.on('SIGTERM', shutdown);

      // Start auto-dev
      try {
        await autoDev.start();
        
        const status = autoDev.getStatus();
        console.log(pc.green('\n✅ Auto-dev completed successfully'));
        console.log(pc.bold('\n📊 Summary:'));
        console.log(`  Total tasks: ${status.completedTasks + status.failedTasks}`);
        console.log(`  Completed: ${status.completedTasks}`);
        console.log(`  Failed: ${status.failedTasks}`);
        
        if (status.startTime && status.endTime) {
          const duration = status.endTime.getTime() - status.startTime.getTime();
          console.log(`  Duration: ${Math.round(duration / 60000)} minutes`);
        }
      } catch (error) {
        console.log(pc.red(`\n❌ Auto-dev failed: ${error}`));
      }
    }));

  // Stop auto-dev mode
  command
    .command('stop')
    .description('Stop automated development mode')
    .action(withErrorHandling(async () => {
      console.log(pc.yellow('Stopping auto-dev mode...'));
      
      // This would need IPC or file-based communication to stop a running instance
      console.log(pc.dim('Note: This command stops auto-dev if running in the same process.'));
      console.log(pc.dim('Use Ctrl+C to stop auto-dev running in another terminal.'));
    }));

  // Show auto-dev status
  command
    .command('status')
    .description('Show auto-dev status')
    .action(withErrorHandling(async () => {
      const autoDev = new AutoDev();
      const status = await autoDev.loadStatus();
      
      if (!status) {
        console.log(pc.yellow('No auto-dev status found'));
        return;
      }
      
      console.log(pc.bold('\n📊 Auto-Dev Status:\n'));
      
      if (status.isRunning) {
        console.log(pc.green('Status: RUNNING'));
        if (status.currentTask) {
          console.log(`Current task: ${pc.cyan(status.currentTask)}`);
        }
      } else {
        console.log(pc.yellow('Status: STOPPED'));
      }
      
      if (status.startTime) {
        console.log(`Started: ${new Date(status.startTime).toLocaleString()}`);
      }
      if (status.endTime) {
        console.log(`Ended: ${new Date(status.endTime).toLocaleString()}`);
      }
      
      console.log(`\nCompleted: ${status.completedTasks} tasks`);
      console.log(`Failed: ${status.failedTasks} tasks`);
      console.log(`Total: ${status.totalTasks} tasks`);
      
      if (status.estimatedTimeRemaining) {
        console.log(`\nEstimated time remaining: ${Math.round(status.estimatedTimeRemaining / 60)} minutes`);
      }
      
      // Show recent logs
      if (status.logs.length > 0) {
        console.log(pc.bold('\n📝 Recent Logs:'));
        const recentLogs = status.logs.slice(-10);
        recentLogs.forEach(log => console.log(pc.dim(log)));
      }
    }));

  // Preview auto-dev plan
  command
    .command('preview')
    .description('Preview tasks that would be processed')
    .option('-n, --number <number>', 'Number of tasks to preview', '10')
    .action(withErrorHandling(async (options) => {
      const { WorkflowManager } = await import('../core/workflow-manager');
      const manager = new WorkflowManager();
      
      console.log(pc.bold('\n📋 Auto-Dev Task Preview:\n'));
      
      // Get current task if any
      const taskState = await manager.getTaskState();
      if (taskState?.current) {
        console.log(pc.yellow('Current task in progress:'));
        console.log(`  ${taskState.current.task}`);
        console.log(`  Phase: ${taskState.current.phase}`);
        console.log(`  Priority: ${taskState.current.priority}`);
        console.log();
      }
      
      // Get recommended tasks
      const recommendations = await manager.recommendNextTask();
      if (!recommendations || recommendations.length === 0) {
        console.log(pc.yellow('No tasks available for auto-dev'));
        return;
      }
      
      console.log(pc.bold('Upcoming tasks:'));
      const limit = parseInt(options.number);
      recommendations.slice(0, limit).forEach((rec, index) => {
        console.log(`\n${index + 1}. ${pc.cyan(rec.task)}`);
        console.log(`   Priority: ${rec.priority}`);
        console.log(`   Reason: ${rec.reason}`);
        if (rec.estimatedDuration) {
          console.log(`   Estimated: ${rec.estimatedDuration} minutes`);
        }
      });
      
      // Calculate total time
      const totalMinutes = recommendations
        .slice(0, limit)
        .reduce((sum, rec) => sum + (rec.estimatedDuration || 60), 0);
      
      console.log(pc.bold(`\n⏱️  Total estimated time: ${Math.round(totalMinutes / 60)} hours`));
    }));

  return command;
}